import { PhotoCrosshair, usePhotoUrl } from 'features/kestrelViewer'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

import { PhotoReelToolbar } from './PhotoReelToolbar'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8); /* Darken background */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000; /* Above panels/forms (<=2001), below global modals (10000) */
`

const NavButton = styled.button<{ $position: 'left' | 'right' }>`
  position: absolute;
  ${({ $position }) => $position}: 3rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.8);
  z-index: 10;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const PhotoCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 10;
`

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  padding: 1rem;
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const FullImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform 0.1s ease-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
`

const HighResLoadingIndicator = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 10;
  pointer-events: none;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #87ceeb;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
type ImageType = 'original'

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  gap: 8px;
`

const ErrorText = styled.div`
  font-size: 14px;
  opacity: 0.6;
`

interface PhotoReelModalProps {
  photo: KVPhoto
  photos: KVPhoto[]
  onClose: () => void
  onPhotoChange: (photo: KVPhoto) => void
  rotation: number
  onRotationChange: (rotation: number) => void
}

const IMAGE_TYPE_CONFIG = {
  original: { urlType: 'fullSize' as const, alt: 'Full resolution photo' },
} as const

export const PhotoReelModal: React.FC<PhotoReelModalProps> = ({
  photo,
  photos,
  onClose,
  onPhotoChange,
  rotation,
  onRotationChange,
}) => {
  // Viewport state
  const [viewport, setViewport] = React.useState({
    zoom: 1,
    pan: { x: 0, y: 0 },
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    hasMoved: false,
  })

  const currentIndex = photos.findIndex((p) => p.id === photo.id)
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < photos.length - 1

  const rotateClockwise = React.useCallback(() => {
    onRotationChange(rotation + 90)
  }, [rotation, onRotationChange])

  const navigateToPrevious = React.useCallback(() => {
    if (hasPrevious) {
      setViewport({
        zoom: 1,
        pan: { x: 0, y: 0 },
        isDragging: false,
        dragStart: { x: 0, y: 0 },
        hasMoved: false,
      })
      setImageDimensions(null)
      onPhotoChange(photos[currentIndex - 1])
    }
  }, [hasPrevious, currentIndex, photos, onPhotoChange])

  const navigateToNext = React.useCallback(() => {
    if (hasNext) {
      setViewport({
        zoom: 1,
        pan: { x: 0, y: 0 },
        isDragging: false,
        dragStart: { x: 0, y: 0 },
        hasMoved: false,
      })
      setImageDimensions(null)
      onPhotoChange(photos[currentIndex + 1])
    }
  }, [hasNext, currentIndex, photos, onPhotoChange])

  const [currentImageType] = React.useState<ImageType>('original')
  const [imageDimensions, setImageDimensions] = React.useState<{
    width: number
    height: number
  } | null>(null)

  const {
    url: currentImageUrl,
    isLoading: urlLoading,
    isLoadingHighRes,
    error: urlError,
  } = usePhotoUrl(photo, IMAGE_TYPE_CONFIG[currentImageType].urlType)

  const isImageLoading = urlLoading || !currentImageUrl
  const currentImageAlt = IMAGE_TYPE_CONFIG[currentImageType].alt

  // Calculate container dimensions accounting for rotation
  const getContainerDimensions = React.useCallback(() => {
    const maxWidth = window.innerWidth * 0.88 - 32
    const maxHeight = window.innerHeight * 0.88 - 64

    if (!imageDimensions) {
      return {
        width: `${maxWidth}px`,
        height: `${maxHeight}px`,
      }
    }

    // When rotated 90° or 270°, the effective dimensions swap
    const normalizedRotation = rotation % 360
    const isRotated90or270 =
      normalizedRotation === 90 || normalizedRotation === 270
    const effectiveWidth = isRotated90or270
      ? imageDimensions.height
      : imageDimensions.width
    const effectiveHeight = isRotated90or270
      ? imageDimensions.width
      : imageDimensions.height

    const imageAspectRatio = effectiveWidth / effectiveHeight
    const maxAspectRatio = maxWidth / maxHeight

    const [containerWidth, containerHeight] =
      imageAspectRatio > maxAspectRatio
        ? [maxWidth, maxWidth / imageAspectRatio]
        : [maxHeight * imageAspectRatio, maxHeight]

    return {
      width: `${containerWidth}px`,
      height: `${containerHeight}px`,
    }
  }, [imageDimensions, rotation])

  // Event handlers
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !viewport.hasMoved) {
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
  }

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget

    if (!imageDimensions) {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.95 : 1.05

    setViewport((prev) => {
      const newZoom = Math.min(Math.max(prev.zoom * delta, 1), 5)
      const zoomRatio = newZoom / prev.zoom
      const scaledPan = { x: prev.pan.x * zoomRatio, y: prev.pan.y * zoomRatio }

      const containerDims = getContainerDimensions()
      const maxPanX =
        ((newZoom - 1) * (parseInt(containerDims.width) || 200)) / 2
      const maxPanY =
        ((newZoom - 1) * (parseInt(containerDims.height) || 200)) / 2

      return {
        ...prev,
        zoom: newZoom,
        pan: {
          x:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.x, -maxPanX), maxPanX),
          y:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.y, -maxPanY), maxPanY),
        },
      }
    })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewport.zoom <= 1) return
    setViewport((prev) => ({
      ...prev,
      isDragging: true,
      hasMoved: false,
      dragStart: { x: e.clientX - prev.pan.x, y: e.clientY - prev.pan.y },
    }))
  }

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault()
  }

  // Handle escape key to close modal and arrow keys to navigate
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        navigateToNext()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, navigateToPrevious, navigateToNext])

  const projection = photo.projection

  // Calculate crosshair position accounting for zoom, pan, and rotation
  const getCrosshairPosition = () => {
    if (!projection || !imageDimensions) return null

    const containerDimensions = getContainerDimensions()
    const containerWidth = parseInt(containerDimensions.width as string) || 0
    const containerHeight = parseInt(containerDimensions.height as string) || 0

    // The image element uses object-fit: contain, which scales based on ORIGINAL
    // dimensions, not rotated. So we calculate how the original image fits in the
    // (rotation-adjusted) container.
    const containerAspectRatio = containerWidth / containerHeight
    const originalAspectRatio = imageDimensions.width / imageDimensions.height

    let scaledImageWidth: number
    let scaledImageHeight: number
    let offsetX: number
    let offsetY: number

    if (originalAspectRatio > containerAspectRatio) {
      // Original image is wider than container - constrained by width
      scaledImageWidth = containerWidth
      scaledImageHeight = containerWidth / originalAspectRatio
      offsetX = 0
      offsetY = (containerHeight - scaledImageHeight) / 2
    } else {
      // Original image is taller than container - constrained by height
      scaledImageHeight = containerHeight
      scaledImageWidth = containerHeight * originalAspectRatio
      offsetX = (containerWidth - scaledImageWidth) / 2
      offsetY = 0
    }

    // Calculate point position on the scaled original image
    const imageRelativeX = projection.x / projection.originalImageWidth
    const imageRelativeY = projection.y / projection.originalImageHeight

    // Position within the scaled image area (before rotation)
    const baseX = offsetX + imageRelativeX * scaledImageWidth
    const baseY = offsetY + imageRelativeY * scaledImageHeight

    // Container center (rotation origin)
    const centerX = containerWidth / 2
    const centerY = containerHeight / 2

    // Apply rotation around container center
    // CSS transform: translate(pan) scale(zoom) rotate(deg) - applied right-to-left
    const rotationRad = (rotation * Math.PI) / 180
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)
    const relX = baseX - centerX
    const relY = baseY - centerY
    const rotatedX = relX * cos - relY * sin
    const rotatedY = relX * sin + relY * cos

    // Then apply scale from center
    const scaledX = centerX + rotatedX * viewport.zoom
    const scaledY = centerY + rotatedY * viewport.zoom

    // Then apply pan
    const finalX = scaledX + viewport.pan.x
    const finalY = scaledY + viewport.pan.y

    return { x: finalX, y: finalY }
  }

  const crosshairPosition = getCrosshairPosition()

  // Handle global mouse events during dragging
  React.useEffect(() => {
    if (!viewport.isDragging) return

    const handleGlobalMouseMove = (e: MouseEvent) => {
      setViewport((prev) => {
        const newPan = {
          x: e.clientX - prev.dragStart.x,
          y: e.clientY - prev.dragStart.y,
        }

        const containerDims = getContainerDimensions()
        const maxPanX =
          ((prev.zoom - 1) * (parseInt(containerDims.width) || 200)) / 2
        const maxPanY =
          ((prev.zoom - 1) * (parseInt(containerDims.height) || 200)) / 2

        return {
          ...prev,
          hasMoved: true,
          pan: {
            x: Math.min(Math.max(newPan.x, -maxPanX), maxPanX),
            y: Math.min(Math.max(newPan.y, -maxPanY), maxPanY),
          },
        }
      })
    }

    const handleGlobalMouseUp = () => {
      setViewport((prev) => ({ ...prev, isDragging: false }))
      setTimeout(() => {
        setViewport((prev) => ({ ...prev, hasMoved: false }))
      }, 100)
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [
    viewport.isDragging,
    viewport.dragStart,
    viewport.zoom,
    getContainerDimensions,
  ])

  return (
    <ModalOverlay
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}>
      {photos.length > 1 && (
        <>
          <NavButton
            $position='left'
            onClick={(e) => {
              e.stopPropagation()
              navigateToPrevious()
            }}
            disabled={!hasPrevious}
            aria-label='Previous photo'>
            <ChevronLeft size={24} />
          </NavButton>
          <NavButton
            $position='right'
            onClick={(e) => {
              e.stopPropagation()
              navigateToNext()
            }}
            disabled={!hasNext}
            aria-label='Next photo'>
            <ChevronRight size={24} />
          </NavButton>
        </>
      )}

      <ModalContent>
        {!isImageLoading && (
          <PhotoReelToolbar onRotateClockwise={rotateClockwise} />
        )}

        <ImageContainer style={getContainerDimensions()}>
          {urlError ? (
            <ErrorContainer>
              <div>Failed to load {currentImageAlt.toLowerCase()}</div>
              <ErrorText>{urlError}</ErrorText>
            </ErrorContainer>
          ) : (
            <>
              {isImageLoading && <LoadingSpinner />}

              {currentImageUrl && (
                <FullImage
                  src={currentImageUrl}
                  alt={currentImageAlt}
                  draggable={false}
                  onLoad={handleImageLoad}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onDragStart={handleDragStart}
                  style={{
                    display: isImageLoading ? 'none' : 'block',
                    transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom}) rotate(${rotation}deg)`,
                    cursor:
                      viewport.zoom > 1
                        ? viewport.isDragging
                          ? 'grabbing'
                          : 'grab'
                        : 'default',
                  }}
                />
              )}

              {/* Show loading indicator when raw image is loading */}
              {!isImageLoading && isLoadingHighRes && (
                <HighResLoadingIndicator />
              )}

              {crosshairPosition && !isImageLoading && (
                <PhotoCrosshair
                  x={crosshairPosition.x}
                  y={crosshairPosition.y}
                  scale={1.0}
                  originalImageWidth={projection?.originalImageWidth}
                  originalImageHeight={projection?.originalImageHeight}
                />
              )}
            </>
          )}
        </ImageContainer>

        {photos.length > 1 && (
          <PhotoCounter>
            {currentIndex + 1} / {photos.length}
          </PhotoCounter>
        )}
      </ModalContent>
    </ModalOverlay>
  )
}
